import {BoxSelection, BoxSelectionBoxSize, Tooltip} from '@wix/wix-ui-tpa/cssVars';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/src/constants';
import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';
import stylesParams from '../../../../components/ProductPage/stylesParams';
import {ButtonOptionWidth, UserInputType} from '../../../constants';
import {useGlobals, withGlobalProps} from '../../../providers/globalPropsProvider';
import {useIsRenderedFrom} from '../../../providers/renderContext';
import {SPECS} from '../../../specs';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import s from './OptionsButtons.scss';
import {st, classes as boxSelectionStyles} from './OptionsButtons.st.css';

export interface OptionsButtonsProps extends IProvidedTranslationProps {
  optionIndex: number;
  showErrors?: boolean;
}

export enum OptionsButtonsDataHook {
  Container = 'options-buttons-container',
  Buttons = 'options-buttons',
  Title = 'options-buttons-title',
}

const OptionsButtonsComponent: React.FC<OptionsButtonsProps> = ({optionIndex, t, showErrors = true}) => {
  const globals = useGlobals();
  const isRenderedFromStickyDrawer = useIsRenderedFrom('stickyDrawer');
  const buttonContainerRef = useRef<HTMLDivElement>(null);

  const {
    product,
    userInputs,
    variantInfo,
    isBackInStockEnabled,
    handleUserInput,
    validateUserInputsChanges,
    scrollAndFocusInputTarget,
    isQuickView,
    isProductSubmitted,
    userInputErrors,
    styles,
    experiments,
    useExperiments,
  } = globals;

  const isStickyAddToCartEnabled = useExperiments.enabled(SPECS.StickyAddToCartMobile);
  const isStickyDrawer = isStickyAddToCartEnabled && isRenderedFromStickyDrawer;

  const isButtonsGridEnabled = experiments.galleryOptionButtonsGrid;
  const buttonOptionWidth = styles.get(stylesParams.productPage_buttonOptionWidth);
  const isGridMode = isButtonsGridEnabled && buttonOptionWidth === ButtonOptionWidth.STRETCH;

  useEffect(() => {
    if (!scrollAndFocusInputTarget || !buttonContainerRef.current) {
      return;
    }

    const isScrollAndFocusInputTarget =
      UserInputType.Selection === scrollAndFocusInputTarget?.inputType &&
      optionIndex === scrollAndFocusInputTarget?.index;
    /* istanbul ignore else */
    if (isScrollAndFocusInputTarget) {
      scrollAndFocusUserInput(UserInputType.Selection, buttonContainerRef.current, isQuickView);
    }
  }, [scrollAndFocusInputTarget, optionIndex, isQuickView]);

  const productOption = product?.options?.[optionIndex];

  const handleChange = ({id: selectionId}: {id: string}) => {
    const nextSelection = productOption.selections.find(({id}) => id.toString() === selectionId);

    handleUserInput(UserInputType.Selection, nextSelection, optionIndex);
    validateUserInputsChanges();
  };

  const activeSelection = userInputs?.[UserInputType.Selection]?.[optionIndex];

  const getSelectionAvailabilityInfo = (selectionId: number) => {
    return variantInfo.selectionsAvailability[productOption.id][selectionId];
  };

  const getVisibleSelections = () => {
    return productOption.selections
      .filter((selection) => getSelectionAvailabilityInfo(selection.id).isVisible)
      .map((selection) => {
        const availabilityInfo = getSelectionAvailabilityInfo(selection.id);
        const isSelectable = availabilityInfo.isSelectable;
        return {...selection, isSelectable};
      });
  };

  const renderBoxOption = (selection: ReturnType<typeof getVisibleSelections>[number]) => {
    const isChecked = activeSelection?.id === selection.id;
    const isDisabled = !isBackInStockEnabled && !selection.isSelectable;

    const boxSelectionOption = (
      <BoxSelection.Option
        key={selection.id}
        id={selection.id.toString()}
        unstableHasInnerWrapper={false}
        checked={isChecked}
        aria-label={selection.description}
        data-hook={`box-selection-option-${selection.id}`}
        className={classNames(s.buttonOptionItemContainer, {
          [s.gridMode]: isGridMode,
          [s.outOfStockLook]: isBackInStockEnabled && !selection.isSelectable,
        })}
        size={BoxSelectionBoxSize.small}
        disabled={isDisabled}>
        <div
          data-hook={`box-selection-text-option-${selection.id}`}
          className={classNames(s.optionItemText, {
            [s.disabled]: !selection.isSelectable,
            [s.selected]: isChecked,
            [s.gridMode]: isGridMode,
            [s.stickyDrawer]: isStickyDrawer,
          })}>
          {selection.description}
        </div>
      </BoxSelection.Option>
    );

    if (isGridMode) {
      return (
        <Tooltip content={selection.description} key={selection.id}>
          {boxSelectionOption}
        </Tooltip>
      );
    }

    return boxSelectionOption;
  };

  const getBoxSelectionOptions = () => getVisibleSelections().map(renderBoxOption);

  const {title} = productOption;
  const shouldShowErrors = showErrors || !isStickyAddToCartEnabled;
  const hasError = isProductSubmitted && userInputErrors?.[UserInputType.Selection]?.[optionIndex];
  const errorProps = shouldShowErrors
    ? {error: hasError, errorMessage: t('SELECT_OPTION_WARNING', {optionTitle: title})}
    : {};

  return (
    <div
      data-hook={OptionsButtonsDataHook.Container}
      id={`${VIEW_MORE_OPTIONS_URL_FRAGMENT}-${optionIndex}`}
      ref={buttonContainerRef}>
      <BoxSelection
        required
        aria-required
        label={title}
        name={`options-buttons-${optionIndex}`}
        aria-label={title}
        size={BoxSelectionBoxSize.xSmall}
        onChange={handleChange}
        className={classNames(
          st(boxSelectionStyles.optionItem, {stickyDrawer: isStickyDrawer}),
          st(boxSelectionStyles.optionsLabel, {stickyDrawer: isStickyDrawer}),
          s.fitOptionItem,
          {
            [s.quickView]: isQuickView,
            [s.gridMode]: isGridMode,
            [boxSelectionStyles.gridMode]: isGridMode,
          }
        )}
        grid={isGridMode}
        data-hook={OptionsButtonsDataHook.Buttons}
        {...errorProps}>
        {getBoxSelectionOptions()}
      </BoxSelection>
    </div>
  );
};

export const OptionsButtons: React.FC<OptionsButtonsProps> = withGlobalProps(
  withTranslations('globals.texts')(OptionsButtonsComponent)
);
